import { State } from '@autopylot-internal/state';
import { AircraftResponse } from './FleetManagmentClient.ts';
import { AlertPresenter } from '../app/AlertBinder.tsx';
import { TrackingService } from '../support/tracking/TrackingService.ts';
import { AIRCRAFT_STATUS_LABELS, AIRCRAFT_TYPE_LABELS } from './labels.ts';

type IFormCallback = (_form: Partial<AircraftResponse>) => Promise<void>;

export class AircraftFormState extends State<Record<string, never>> {
    static inject = () => [
        TrackingService,
        AlertPresenter
    ];
    constructor(
        private tracker: TrackingService,
        private alert: AlertPresenter
    ) {
        super({});
    }

    initial: Partial<AircraftResponse> = {};
    form: Partial<AircraftResponse> = {};
    isOpen = false;
    isMenuOpen = false;
    isEditing = false;

    start = (params: { form: Partial<AircraftResponse>, isOpen?: boolean, isEditing?: boolean, onSave?: IFormCallback, onDelete?: IFormCallback }) => {
        this.form = {
            nickname: '',
            status: 'active',
            frameType: 'rotary-wing',
            make: '',
            model: '',
            ...params.form,
        };
        this.initial = { ...this.form };
        this.onSave = params.onSave;
        this.onDelete = params.onDelete;
        this.isOpen = params.isOpen || false;
        this.isEditing = params.isEditing || false;
        this.isMenuOpen = false;
        this.setState({});
    };

    edit = () => {
        this.isMenuOpen = false;
        this.isEditing = true;
        this.setState({});
    };

    getChanges = (): Partial<AircraftResponse> => {
        const changes: any = {};
        for (const k of Object.keys(this.form) as Array<keyof AircraftResponse>) {
            if (this.initial[k] !== this.form[k]) {
                changes[k] = this.form[k];
            }
        }
        return changes;
    };

    cancel = () => {
        this.form = this.initial;
        this.isEditing = false;
        this.isOpen = false;
        this.setState({});
    };

    openMenu = () => {
        this.isMenuOpen = true;
        this.setState({});
    };

    closeMenu = () => {
        this.isMenuOpen = false;
        this.setState({});
    };

    autoFocus = () => {
        // only auto focus on add, which we can infer from can delete
        return !this.canDelete();
    };

    change = (updates: Partial<AircraftResponse>) => {
        Object.assign(this.form, updates);
        this.setState({ });
    };

    private onSave?: (form: Partial<AircraftResponse>) => Promise<void>;
    private onDelete?: (form: Partial<AircraftResponse>) => Promise<void>;

    private buildTrackingMeta = (a: Partial<AircraftResponse>) => {
        const meta: any = {
            equipmentNickname: a.nickname,
            equipmentMake: a.make,
            equipmentModel: a.model,
            equipmentFrameType: a.frameType ? AIRCRAFT_TYPE_LABELS[a.frameType] : undefined,
            equipmentStatus: a.status ? AIRCRAFT_STATUS_LABELS[a.status] : undefined,
            equipmentSerialNumber: a.serial,
            equipmentValue: a.value,
            equipmentWeight: a.weight,
            equipmentFAARegistrationNumber: a.faaRegistrationNumber,
            equipmentFAARegistrationDate: a.faaRegistrationDate
        };
        Object.keys(meta).forEach(key => meta[key] === undefined && delete meta[key]);
        return meta;
    };

    save = async () => {
        if (!this.onSave){
            throw new Error('save called when no onSave handler');
        }
        try {
            await this.onSave(this.form);

            if (!this.form.aircraftId) {
                this.tracker.track('Equipment Added', () => ({
                    equipmentType: 'Drone',
                    ...this.buildTrackingMeta(this.form)
                }));

            } else {
                const changes = this.getChanges();
                this.tracker.track('Equipment Edited', () => ({
                    equipmentType: 'Drone',
                    changes: this.buildTrackingMeta(changes)
                }));
            }

            this.isEditing = false;
            this.isOpen = false;
            this.initial = { ...this.form };
            this.setState({});
        }
        catch (err: any){
            if (['nickname.too_small', 'nickname.required'].includes(err.errorCode)){
                this.alert.showAlertV2({ header: 'Nickname is reqiured' });
            }
            else {
                this.alert.showAndLogError(err);
            }
        }
    };

    canDelete = () => {
        return Boolean(this.onDelete);
    };

    delete = async () =>  {
        if(!this.onDelete){
            throw new Error('delete called when no onDelete');
        }

        await this.onDelete(this.form);

        this.tracker.track('Equipment Deleted', () => ({
            equipmentType: 'Drone',
            ...this.buildTrackingMeta(this.form)
        }));

        this.isEditing = false;
        this.isOpen = false;
        this.setState({});
    };
}
